import { DataField } from '../models/data-field';

/**
 * Field delimiter (comma)
 */
export const FIELD_DELIMITER = ',';

/**
 * Double quote character
 */
export const DOUBLE_QUOTE_CHARACTER = '"';

/**
 * Line delimiter (carriage return + newline)
 */
export const LINE_DELIMITER = '\r\n';

/**
 * File creator utility. Provides ability to create file content from tabular
 * data structures (i.e. the grid widget).
 *
 * TODO: This, along with DataField should ideally be moved into pds-library
 */
export let fileCreator = {

  /**
   * Function to create a CSV file from provided tabular data structures.
   *
   * @param rows
   * @param fields
   */
  createCSV(rows: Array<any>, fields: Array<DataField>): string {
    // Variable that will contain final content to be returned
    let content = '';
    // Create the header line by looping through fields
    let fieldNum = 0;
    for (let field of fields) {
      if (fieldNum === 0) {
        content += fileCreator.buildValidCSVFieldValue(field.title);
      } else {
        content += FIELD_DELIMITER + fileCreator.buildValidCSVFieldValue(field.title);
      }
      fieldNum += 1;
    }
    // Loop through each data row
    for (let row of rows) {
      fieldNum = 0;
      content += LINE_DELIMITER;
      // Loop through each field
      for (let field of fields) {
        if (fieldNum === 0) {
          content += fileCreator.buildValidCSVFieldValue(field.resolveAsString(row));
        } else {
          content += FIELD_DELIMITER + fileCreator.buildValidCSVFieldValue(field.resolveAsString(row));
        }
        fieldNum += 1;
      }
    }
    return content;
  },

  /**
   * Utility to build a CSV-valid field value. Handles escaping double-quotes
   * and adding surrounding double quotes as needed (for example, if field data
   * contains the delimiter character).
   */
  buildValidCSVFieldValue(source: string): string {
    // Is the source string non-empty?
    if (source != null && source.trim().length > 0) {
      // The source string is non-empty. Start by trimming any whitespace from
      // the ends.
      let trimmedSource = source.trim();
      // Does the trimmed source string contain the double quote, field
      // delimiter, newline or carriage return characters?
      if (trimmedSource.includes(DOUBLE_QUOTE_CHARACTER)
        || trimmedSource.includes(FIELD_DELIMITER)
        || trimmedSource.includes('\n')
        || trimmedSource.includes('\r')) {
        // The trimmed source contains double quote, field delimiter, newline
        // or carriage return characters. Does the trimmed source string
        // contain double quotes specifically?
        if (trimmedSource.includes(DOUBLE_QUOTE_CHARACTER)) {
          // The trimmed source does contain a double quote character
          // specifically. Now does the trimmed source begin and end with a
          // double quotes?
          if (trimmedSource.startsWith(DOUBLE_QUOTE_CHARACTER)
            && trimmedSource.endsWith(DOUBLE_QUOTE_CHARACTER)
            && trimmedSource.length >= 2) {
            // The trimmed source does begin and end with double quotes, so let
            // us remove them for a moment.
            let endQuoteTrimmedSource = trimmedSource.substring(1, trimmedSource.length - 1);
            // Escape any other potential double quotes, restore the double
            // quotes on the end, then return.
            return DOUBLE_QUOTE_CHARACTER
              + endQuoteTrimmedSource.replace(/"/g, '""')
              + DOUBLE_QUOTE_CHARACTER;
          } else {
            // The trimmed source does not have double quotes on both ends, but
            // it must have at least one double quote somewhere, so escape it,
            // add double quotes and return. This should also cover the case
            // where there are any of field delimiter, newline or carriage
            // return characters in addition to a double quote.
            return DOUBLE_QUOTE_CHARACTER +
              trimmedSource.replace(/"/g, '""') +
              DOUBLE_QUOTE_CHARACTER;
          }
        } else {
          // The trimmed source does not include a double quote, so it must
          // contain the field delimiter, newline or carriage return character
          // at least. Therefore, put double quotes around it and return.
          return DOUBLE_QUOTE_CHARACTER + trimmedSource + DOUBLE_QUOTE_CHARACTER;
        }
      } else {
        // Trimmed source does not contain any of double quotes, field
        // delimiter, newline or carriage return characters, so return as is.
        return trimmedSource;
      }
    } else {
      // Source string is just whitespace at most, so return empty string.
      return '';
    }
  }

};
